// @ts-strict-ignore
import React, { FC, useState, useEffect, useRef } from 'react'

import { getDirMode, formatNumberStyle } from '../../../platform'
import SectionHeader from './SectionHeader'
import classNames from 'classnames'
import {
	ScreenSection,
	SCREEN_SECTION_TYPE_AUDIO,
	ScreenSectionItem,
	SCREEN_SECTION_TYPE_IMAGE,
	SCREEN_SECTION_TYPE_VIDEO,
	SCREEN_SECTION_TYPE_WEB_VIDEO,
	SCREEN_SECTION_TYPE_AR,
	SCREEN_SECTION_TYPE_ANIMATION,
	Collection,
	Screen,
} from 'mytours-read-common'
import { useMediaQuery } from 'react-responsive'
import SectionMediaAudioComponent from './SectionMediaAudio'
import SectionMediaVideoComponent from './SectionMediaVideo'
import SectionMediaImage from './SectionMediaImage'
import { GetScreensShowResponseSections as SingleMediaSection } from 'mytours-read-api-typescript'
import Modal from '../../../common/components/modal'
import { getImageHeight } from './functions'
import { ScreenSize } from 'common_lib/types'
import { getScreenSize } from 'common_lib/utils'
import { EventActionsEnum, sendEvent } from 'mobile_web/modules/logging/analytics'
import { Button, Grid, GridRow } from '@mytours/components'
import { getCollapseShadowStyle } from '../../functions'
import strings from '../../../../strings'
import FullscreenMedia from './FullscreenMedia'
import '../styles/sectionSingleMedia.less'
import '../styles/sectionCollapsable.less'
import { getImageAlt } from 'mobile_web/modules/common/functions'

interface SectionSingleMediaProps {
	section: ScreenSection
	collection: Collection
	screen: Screen
	isInsideModal: boolean
}

const SectionSingleMedia: FC<SectionSingleMediaProps> = ({ section, collection, screen, isInsideModal }) => {
	const [showMediaModal, setShowMediaModal] = useState<boolean>(false)
	const [modalPresented, setModalPresented] = useState<boolean>(false)
	const [screenSize, setScreenSize] = useState<ScreenSize>(getScreenSize())
	const [imageHeight, setImageHeight] = useState<number>(getImageHeight(screenSize))
	const [isCollapsed, setCollapsed] = useState(false)
	const [blockHeight, setBlockHeight] = useState<number>()
	const contentRef = useRef<HTMLDivElement>()

	const isTabletOrMobile = useMediaQuery({ maxWidth: 1024.5 })

	useEffect(() => {
		const interval = setInterval(() => {
			if (contentRef?.current?.clientHeight !== 0 && !blockHeight && section?.collapsable) {
				setBlockHeight(contentRef?.current.clientHeight * section.exposable_content * 0.01)
				setCollapsed(section.exposable_content !== 100)
				clearInterval(interval)
			}
		}, 50)

		return () => clearInterval(interval)
	}, [contentRef?.current, section])

	const setImageDimension = () => {
		const newScreenSize = getScreenSize()
		if (screenSize !== newScreenSize) {
			setImageHeight(getImageHeight(screenSize))
			setScreenSize(newScreenSize)
		}
	}

	useEffect(() => {
		setImageDimension()
		addEventListener('resize', setImageDimension)

		return () => {
			removeEventListener('resize', setImageDimension)
		}
	}, [])

	const getMediaItemClass = (): string => {
		switch (section.text_position) {
			case SingleMediaSection.TextPositionEnum.Right:
			case SingleMediaSection.TextPositionEnum.Left:
				return `half-width floated-${
					section.text_position === SingleMediaSection.TextPositionEnum.Left ? 'right' : 'left'
				}`
			case SingleMediaSection.TextPositionEnum.Bottom:
			case SingleMediaSection.TextPositionEnum.Top:
			case SingleMediaSection.TextPositionEnum.None:
				return 'full-width'
			default:
				return ''
		}
	}

	const toggleFullscreenMediaModal = (): void => {
		if (!section.zoomable) return

		if (!showMediaModal) {
			sendEvent(EventActionsEnum.OpenLightbox, {
				collection: collection?.name,
				collectionId: collection?.id,
				itemName: screen.name,
			})
		}

		setShowMediaModal(!showMediaModal)
	}

	const renderItem = (sectionItem: ScreenSectionItem): JSX.Element => {
		let sectionItemComponent: JSX.Element

		const { id: sectionId, name } = section

		switch (sectionItem.media_type) {
			case SCREEN_SECTION_TYPE_AUDIO:
				sectionItemComponent = (
					<Grid className="vertical-audio">
						<SectionMediaAudioComponent
							collectionId={collection?.id}
							container={{ type: 'section', id: sectionId, title: name }}
							audioPreview={sectionItem}
							screen={screen}
						/>
					</Grid>
				)
				break
			case SCREEN_SECTION_TYPE_VIDEO:
			case SCREEN_SECTION_TYPE_WEB_VIDEO:
				sectionItemComponent = (
					<GridRow className="full-width">
						{' '}
						<SectionMediaVideoComponent
							onPlay={null}
							sectionItem={sectionItem}
							isSingleItem={true}
							height={imageHeight}
							sectionId={section.id}
							sectionTitle={section.name}
							containerName={section.name}
						/>
					</GridRow>
				)
				break
			case SCREEN_SECTION_TYPE_IMAGE:
			case SCREEN_SECTION_TYPE_ANIMATION:
			case SCREEN_SECTION_TYPE_AR:
				{
					const thumbnailMediaItem = sectionItem?.thumbnail_media_item ?? sectionItem
					const thumbnailFile = thumbnailMediaItem?.file
					sectionItemComponent = (
						<div
							className={classNames(
								'full-width',
								{ 'audio-big': !thumbnailFile },
								{ 'not-zoomable': !section.zoomable },
							)}
							onClick={() => toggleFullscreenMediaModal()}
							role="button"
						>
							{thumbnailFile ? (
								<SectionMediaImage file={thumbnailFile} alt={getImageAlt(thumbnailMediaItem)} />
							) : (
								<GridRow className="cover-image" style={{ height: formatNumberStyle(imageHeight) }}>
									<Button
										variant="clear"
										onClick={() => toggleFullscreenMediaModal()}
										aria-label={strings.play_media}
										className="controls controls-small media-action-click"
									>
										<i className="fal fa-head-side-goggles" />
									</Button>
								</GridRow>
							)}
						</div>
					)
				}
				break
			default:
				console.warn(`sectionItem Type : ${sectionItem.media_type}`)
				sectionItemComponent = <div />
				break
		}

		return <React.Fragment key={sectionItem.id}>{sectionItemComponent}</React.Fragment>
	}

	const onModalDidPresent = (): void => setModalPresented(true)

	const onModalDidDismiss = (): void => {
		setShowMediaModal(false)
		setModalPresented(false)
	}

	const renderFullscreenMediaModal = (): JSX.Element => {
		const { media_item: mediaItem } = section

		return (
			<Modal
				isOpen={showMediaModal}
				cssClass="media-modal"
				showBackdrop={true}
				onDidDismiss={onModalDidDismiss}
				onDidPresent={onModalDidPresent}
			>
				{modalPresented && (
					<FullscreenMedia
						collectionId={collection?.id}
						mediaData={[mediaItem]}
						activeSlideIndex={0}
						onBack={() => toggleFullscreenMediaModal()}
						section={section}
						screen={screen}
					/>
				)}
			</Modal>
		)
	}

	const {
		media_item: mediaItem,
		description,
		text_position: position,
		collapsable,
		exposable_content: exposableContent,
	} = section

	const renderCollapseButton = (): React.ReactElement => (
		<Button onClick={() => setCollapsed(!isCollapsed)} className="collapse-btn" variant="clear" dir={getDirMode()}>
			<i className={`fas fa-caret-${isCollapsed ? 'down' : 'up'}`} />
			<span>{isCollapsed ? strings.expand : strings.collapse}</span>
		</Button>
	)

	const isFullyCollapsed = collapsable && (exposableContent === 0 || exposableContent === 100 || blockHeight < 85)

	const getCollapsedStyles = () => {
		if (isCollapsed && exposableContent !== 0) {
			return {
				maxHeight: `${exposableContent === 100 ? 34 : blockHeight}px`,
				overflow: 'hidden',
				height: `${exposableContent === 100 ? 34 : blockHeight}px`,
			}
		}
	}

	return (
		<Grid
			dir={getDirMode()}
			className={classNames('section-single-media', {
				'section-collapsable': collapsable,
				'-collapsed': isCollapsed,
				'-fully-collapsable': isFullyCollapsed,
			})}
			style={getCollapsedStyles()}
			ref={contentRef}
		>
			<SectionHeader title={section?.title} noOverflow={isCollapsed} />
			{isFullyCollapsed && renderCollapseButton()}
			{((isFullyCollapsed && !isCollapsed) || !isFullyCollapsed) && (
				<GridRow className="block-content" data-cy="story-screen-section-content">
					<div className={`content-wrap text-${position}`}>
						<div className={`media-wrap ${getMediaItemClass()}`}>{!!mediaItem && renderItem(mediaItem)}</div>
						{position !== SingleMediaSection.TextPositionEnum.None && (
							<p className="description" dangerouslySetInnerHTML={{ __html: description }} />
						)}
						{collapsable && exposableContent !== 0 && exposableContent !== 100 && blockHeight >= 85 && (
							<div
								className={classNames('collapse-shadow', {
									'mobile-or-tablet-collapsed': (isTabletOrMobile || isInsideModal) && isCollapsed,
								})}
								style={{ background: getCollapseShadowStyle() }}
							>
								{renderCollapseButton()}
							</div>
						)}
					</div>
				</GridRow>
			)}
			{showMediaModal && renderFullscreenMediaModal()}
		</Grid>
	)
}

export default SectionSingleMedia
